import { TypedDocumentNode, gql } from '@apollo/client';
import { PropsWithChildren, useEffect, useRef, useState } from 'react';

import { useUpdateSawBoxOnboarding } from '@sorare/core/src/hooks/rivals/useUpdateSawBoxOnboarding';

import { BoxOpeningOnboardingDialog } from 'components/rewards/BoxOpeningOnboardingDialog';
import { useBoxOpeningOnboardingDialog } from 'components/rewards/BoxOpeningOnboardingDialog/useBoxOpeningOnboardingDialog';
import { RewardBoxDialog } from 'components/rewards/RewardBoxDialog';

import { RewardBoxDialogWithOnboarding_probabilisticBundle } from './__generated__/index.graphql';

type Props = {
  open: boolean;
  onClose: () => void;
  bundle: RewardBoxDialogWithOnboarding_probabilisticBundle;
  disableOnClose?: boolean;
};
export const RewardBoxDialogWithOnboarding = ({
  open,
  onClose,
  bundle,
  disableOnClose,
  children,
}: PropsWithChildren<Props>) => {
  const [onboardingDialogOpen, setOnboardingDialogOpen] = useState(false);

  const updateSawBoxOnboarding = useUpdateSawBoxOnboarding();

  const openingOnboardingDialogVariant = useBoxOpeningOnboardingDialog(bundle);

  const openOnboardingDialogTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  if (openOnboardingDialogTimeoutRef.current && onboardingDialogOpen) {
    clearTimeout(openOnboardingDialogTimeoutRef.current);
    openOnboardingDialogTimeoutRef.current = null;
  }

  useEffect(() => {
    return () => {
      if (openOnboardingDialogTimeoutRef.current) {
        clearTimeout(openOnboardingDialogTimeoutRef.current);
      }
    };
  }, []);

  const showOnboardingDialog = !!openingOnboardingDialogVariant;

  const onCloseOnboardingDialog = () => {
    if (openingOnboardingDialogVariant === 'limited_card') {
      updateSawBoxOnboarding({ limitedCard: true });
    }
    if (openingOnboardingDialogVariant === 'arena_ticket') {
      updateSawBoxOnboarding({ arenaTickets: true });
    }
    if (openingOnboardingDialogVariant === 'limited_essence') {
      updateSawBoxOnboarding({ essence: true });
    }
    setOnboardingDialogOpen(false);
    onClose();
  };

  return (
    <>
      <RewardBoxDialog
        open={open}
        onClose={() => {
          if (showOnboardingDialog) {
            setOnboardingDialogOpen(true);
          } else {
            onClose();
          }
        }}
        onClaimed={
          showOnboardingDialog
            ? () => {
                openOnboardingDialogTimeoutRef.current = setTimeout(() => {
                  setOnboardingDialogOpen(true);
                }, 2000);
              }
            : undefined
        }
        rewardBoxes={[bundle]}
        disableOnClose={disableOnClose}
        disableContinue={showOnboardingDialog}
      >
        {children}
      </RewardBoxDialog>
      {onboardingDialogOpen && (
        <BoxOpeningOnboardingDialog
          open
          onClose={onCloseOnboardingDialog}
          bundle={bundle}
          disableOnClose={disableOnClose}
        />
      )}
    </>
  );
};
RewardBoxDialogWithOnboarding.fragments = {
  probabilisticBundle: gql`
    fragment RewardBoxDialogWithOnboarding_probabilisticBundle on ProbabilisticBundle {
      id
      ...RewardBoxDialog_ProbabilisticBundle
      ...BoxOpeningOnboardingDialog_probabilisticBundle
      ...useBoxOpeningOnboardingDialog_probabilisticBundle
    }
    ${RewardBoxDialog.fragments.ProbabilisticBundle}
    ${BoxOpeningOnboardingDialog.fragments.probabilisticBundle}
    ${useBoxOpeningOnboardingDialog.fragments.probabilisticBundle}
  ` as TypedDocumentNode<RewardBoxDialogWithOnboarding_probabilisticBundle>,
};
